import { Alert, AlertTitle, AsyncButton, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
// import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AnimatePresence, motion, useAnimate, usePresence } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Boundary, Page } from '~/components'
import {
  AgentInfoCard,
  HitlQueueCard,
  HitlQueueCardSkeleton,
  PerformanceCard,
  PerformanceCardSkeleton,
  RecentConversationsCard,
  RecentConversationsCardSkeleton,
} from '~/features/bots/components/overview'
import { AgentAnalyticsContextProvider } from '~/features/bots/components/overview/analytics/AgentAnalyticsContext'
import { AnalyticsChart, AnalyticsChartSkeleton } from '~/features/bots/components/overview/analytics/AnalyticsChart'
import { AnalyticsDateRangeSelector } from '~/features/bots/components/overview/analytics/AnalyticsDateRangeSelector'
import {
  AnalyticsOverview,
  AnalyticsOverviewSkeleton,
} from '~/features/bots/components/overview/analytics/AnalyticsOverview'
import { InsightsCard } from '~/features/bots/components/overview/InsightsCard'
import { BotAnalyticsMetricKeys } from '~/features/bots/types'
import { useUpdateAlwaysAlive } from '~/hooks'
import { useSuspenseQuery } from '~/services'

export const MAX_BOTNAME_LENGTH = 64

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/overview')({
  component: Component,
})

const AlwaysAliveAlert = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const { mutate: updateAlwaysAlive, isPending } = useUpdateAlwaysAlive()
  const AlertMotion = motion(Alert)
  const [scope, animate] = useAnimate()
  const [isPresent, safeToRemove] = usePresence()
  const [enterAnimationActive, setEnterAnimationActive] = useState(true)

  useEffect(() => {
    if (isPresent) {
      const enterAnimation = async () => {
        await animate(
          scope.current,
          { opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 },
          { duration: 0.15, ease: 'easeOut' }
        )
      }
      enterAnimation()
    } else {
      const exitAnimation = async () => {
        await animate(
          scope.current,
          { opacity: 0, y: '-50%', filter: 'blur(3px)', scale: 0.95 },
          { duration: 0.15, ease: 'easeOut' }
        )
        safeToRemove()
      }

      exitAnimation()
    }
  }, [isPresent, animate, scope, safeToRemove])

  return (
    <AlertMotion
      ref={scope}
      initial={isPresent && enterAnimationActive ? { opacity: 0, y: '-50%', filter: 'blur(3px)', scale: 0.95 } : false}
      className="flex flex-col items-center justify-between gap-3 border-none bg-amber-3 px-4 py-3 sm:flex-row sm:gap-4"
    >
      <AlertTitle className="m-0 flex items-center gap-6 text-amber-11">
        <AlertCircle size={16} className="flex-none self-center" />
        <span className="text-sm">
          Your agent may sleep when inactive, adding a few seconds to its first reply. Enable Always Alive for instant
          responses every time.
        </span>
      </AlertTitle>
      <AsyncButton
        variant="outline"
        size="sm"
        className="h-9 w-full text-foreground sm:h-6 sm:w-auto"
        loading={isPending}
        disabled={isPending}
        onClick={() => {
          setEnterAnimationActive(false)
          showConfirmationPrompt(
            <span>
              Always Alive ensures the Agent runs continuously, enabling faster response times when users first initiate
              a conversation
            </span>,
            {
              title: 'Enable Always Alive',
              confirmLabel: 'Enable Always Alive',
            }
          ).then(() =>
            updateAlwaysAlive({
              botId,
              workspaceId,
              alwaysAlive: true,
            })
          )
        }}
      >
        Enable Always Alive
      </AsyncButton>
    </AlertMotion>
  )
}

function Component() {
  const { user } = Route.useRouteContext()
  const { workspaceId, botId } = Route.useParams()

  const {
    data: { alwaysAlive },
  } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    workspaceId,
    botId,
  })

  return (
    <Page>
      <AgentAnalyticsContextProvider>
        <div className="mb-6 flex flex-col gap-6 md:mb-4 md:gap-4">
          <AgentInfoCard workspaceId={workspaceId} botId={botId} userId={user.id} />
          <AnimatePresence>
            {alwaysAlive ? null : <AlwaysAliveAlert workspaceId={workspaceId} botId={botId} />}
          </AnimatePresence>

          <div className="flex w-full flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <h2 className="w-full text-xl font-bold">Overview</h2>
            <AnalyticsDateRangeSelector className="w-full sm:w-auto" />
          </div>

          <Boundary
            suspenseFallback={
              <div className="contents md:hidden">
                {BotAnalyticsMetricKeys.map((metric) => (
                  <AnalyticsOverviewSkeleton key={metric} />
                ))}
              </div>
            }
          >
            <div className="contents md:hidden">
              {BotAnalyticsMetricKeys.map((metric) => (
                <AnalyticsOverview
                  key={metric}
                  botId={botId}
                  workspaceId={workspaceId}
                  metric={metric}
                  className="w-full"
                />
              ))}
            </div>
          </Boundary>
        </div>

        <div className="flex flex-wrap gap-6 md:gap-4">
          <div className="flex w-0 basis-full flex-col gap-6 max-lg:contents md:gap-4 lg:basis-[calc(67%-8px)] xl:basis-[calc(75%-8px)]">
            <Boundary suspenseFallback={<AnalyticsChartSkeleton className="order-1 w-full" />}>
              <AnalyticsChart botId={botId} workspaceId={workspaceId} className="order-1 w-full" />
            </Boundary>
            <InsightsCard className="order-last" />
          </div>

          <div className="flex h-fit w-0 basis-full flex-row items-end gap-6 max-lg:contents md:flex-col lg:basis-[calc(33%-8px)] lg:gap-4 xl:basis-[calc(25%-8px)]">
            <Boundary
              suspenseFallback={
                <HitlQueueCardSkeleton
                  workspaceId={workspaceId}
                  botId={botId}
                  className="order-2 w-0 basis-full lg:w-full"
                />
              }
            >
              <HitlQueueCard workspaceId={workspaceId} botId={botId} className="order-2 w-0 basis-full lg:w-full" />
            </Boundary>
            <Boundary
              suspenseFallback={
                <PerformanceCardSkeleton
                  workspaceId={workspaceId}
                  botId={botId}
                  className="order-2 w-0 basis-full sm:max-lg:basis-[calc(50%-8px)] lg:w-full"
                />
              }
            >
              <PerformanceCard
                workspaceId={workspaceId}
                botId={botId}
                className="order-3 w-0 basis-full sm:max-lg:basis-[calc(50%-12px)] lg:w-full"
              />
            </Boundary>
            <Boundary
              suspenseFallback={
                <RecentConversationsCardSkeleton
                  workspaceId={workspaceId}
                  botId={botId}
                  className="order-3 w-0 basis-full sm:max-lg:basis-[calc(50%-8px)] lg:w-full"
                />
              }
            >
              <RecentConversationsCard
                workspaceId={workspaceId}
                botId={botId}
                className="order-4 w-0 basis-full sm:max-lg:basis-[calc(50%-12px)] lg:w-full"
              />
            </Boundary>
          </div>
        </div>
      </AgentAnalyticsContextProvider>
    </Page>
  )
}
